import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, LogIn, Ticket, X, Sparkles, Clock, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api';
import { useAuth } from '@/context/AuthContext';

const PLAN_INFO = {
  starter: { name: 'Starter', price: '$9/mo', perks: ['Exact quote + full breakdown', 'Save & email quotes', 'Material library'] },
  pro:     { name: 'Pro',     price: '$29/mo', perks: ['Custom labor rate + margin controls', 'Complexity multipliers', 'Branded PDF proposals'] },
  fleet:   { name: 'Fleet',   price: '$79/mo', perks: ['Everything in Pro', '5 team seats', 'Batch quoting + API'] },
};

export function useMembershipHref() {
  const { pathname } = useLocation();
  return pathname === '/' ? '#membership' : '/#membership';
}

// Inline "Have a trial pass?" redeem box. Calls onRedeemed(entitlement) on success.
export function TrialPassRedeem({ compact = false, testIdPrefix = 'trial' }) {
  const { refreshEntitlement } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const redeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post('/trial/redeem', { code: code.trim() });
      await refreshEntitlement(true);
      toast.success(`Trial pass activated — ${data.trial?.days_left} days of ${data.tier} access.`);
      setCode('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not redeem that code');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={redeem} className={`flex ${compact ? 'gap-2' : 'flex-col sm:flex-row gap-2'}`} data-testid={`${testIdPrefix}-redeem-form`}>
      <input
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="MI-XXXX-XXXX"
        data-testid={`${testIdPrefix}-redeem-input`}
        className="mi-input font-mono tracking-widest uppercase"
        maxLength={14}
      />
      <button type="submit" disabled={busy || !code.trim()} data-testid={`${testIdPrefix}-redeem-btn`} className="mi-btn-red text-xs whitespace-nowrap disabled:opacity-50">
        <Ticket className="w-3.5 h-3.5" /> {busy ? 'Checking…' : 'Redeem pass'}
      </button>
    </form>
  );
}

export function UpgradePrompt({ tierId, isAuthed, onSignIn, onClose }) {
  const plan = PLAN_INFO[tierId] || PLAN_INFO.starter;
  const membershipHref = useMembershipHref();
  return (
    <div className="mt-3 border-2 border-[#E63946]/50 bg-[#E63946]/[0.05] p-5" data-testid="wrap-upgrade-prompt">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="mi-eyebrow text-[#E63946] flex items-center gap-2">
          <Lock className="w-3.5 h-3.5" /> // {plan.name.toUpperCase()} · PAID MEMBERS ONLY
        </div>
        <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="wrap-upgrade-close" aria-label="Close"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid md:grid-cols-[1fr_auto] gap-5 items-start">
        <div>
          <div className="mi-headline text-2xl md:text-3xl leading-tight mb-2">
            Unlock {plan.name} — <span className="text-[#E63946]">{plan.price}</span>, 7-day free trial.
          </div>
          <ul className="text-sm text-white/70 space-y-1 mb-4">
            {plan.perks.map((p) => <li key={p} className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#E63946]" /> {p}</li>)}
          </ul>
          {!isAuthed ? (
            <button onClick={onSignIn} data-testid="wrap-upgrade-signin" className="mi-btn-red text-sm">
              <LogIn className="w-4 h-4" /> Sign in to start your trial
            </button>
          ) : (
            <a href={membershipHref} data-testid="wrap-upgrade-cta" className="mi-btn-red text-sm">
              <Sparkles className="w-4 h-4" /> Start {plan.name} — 7 days free <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
        {isAuthed && (
          <div className="border mi-line bg-black/50 p-4 md:w-[300px]">
            <div className="mi-eyebrow text-white/60 text-[10px] mb-2 flex items-center gap-1.5"><Ticket className="w-3 h-3" /> HAVE A TRIAL PASS?</div>
            <TrialPassRedeem compact />
            <div className="text-white/40 text-[10px] mt-2">One-time code from Motor iNKS · 30 days full access.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Slim status line under the tier picker: plan source, trial countdown, expired-trial nudge.
export function EntitlementBadge({ entitlement, isAuthed, onSignIn }) {
  const membershipHref = useMembershipHref();
  if (!isAuthed) {
    return (
      <div className="text-[11px] text-white/50 flex items-center gap-2" data-testid="wrap-entitlement-anon">
        <Lock className="w-3 h-3" /> Guest pricing shows a range. Starter, Pro and Fleet unlock with a paid account.
        <button onClick={onSignIn} className="text-[#E63946] hover:underline">Sign in</button>
      </div>
    );
  }
  if (!entitlement) return null;
  const { tier, source, trial, trial_expired, subscription } = entitlement;

  if (trial_expired && source !== 'subscription') {
    return (
      <div className="border border-yellow-400/50 bg-yellow-400/[0.06] p-3 text-xs text-yellow-200 flex flex-wrap items-center gap-3" data-testid="wrap-trial-expired">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span>Your 30-day trial pass has ended. You're back on <strong>Guest</strong> (price ranges only) — convert to a paid plan to keep exact quotes, margin and PDF proposals.</span>
        <a href={membershipHref} data-testid="wrap-trial-convert-cta" className="mi-btn-red text-[11px] py-1.5 px-3 ml-auto">Convert to paid <ArrowRight className="w-3 h-3" /></a>
      </div>
    );
  }
  if (source === 'trial' && trial) {
    return (
      <div className="text-[11px] text-white/70 flex flex-wrap items-center gap-2" data-testid="wrap-trial-active">
        <span className="mi-tag mi-tag-red text-[9px] inline-flex items-center gap-1"><Clock className="w-3 h-3" /> TRIAL PASS · {trial.days_left} DAY{trial.days_left === 1 ? '' : 'S'} LEFT</span>
        Full <strong className="text-white capitalize">{tier}</strong> access until {new Date(trial.expires_at).toLocaleDateString()}.
        <a href={membershipHref} className="text-[#E63946] hover:underline">Convert to paid anytime →</a>
      </div>
    );
  }
  if (source === 'subscription' && subscription) {
    return (
      <div className="text-[11px] text-white/70 flex flex-wrap items-center gap-2" data-testid="wrap-sub-active">
        <span className="mi-tag mi-tag-red text-[9px] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {tier.toUpperCase()} · {subscription.status === 'trialing' ? 'FREE TRIAL' : 'ACTIVE'}</span>
        {subscription.status === 'trialing' && subscription.trial_end && <>Trial ends {new Date(subscription.trial_end * 1000).toLocaleDateString()}.</>}
        {subscription.cancel_at_period_end && <span className="text-yellow-300">Cancels at period end.</span>}
        <Link to="/dashboard" className="text-[#E63946] hover:underline">Manage plan →</Link>
      </div>
    );
  }
  if (source === 'admin') {
    return <div className="text-[11px] text-white/50" data-testid="wrap-admin-access">Admin · all tiers unlocked.</div>;
  }
  if (source === 'comp') {
    return (
      <div className="text-[11px] text-white/70 flex flex-wrap items-center gap-2" data-testid="wrap-comp-active">
        <span className="mi-tag mi-tag-red text-[9px] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {tier.toUpperCase()} · COMPLIMENTARY</span>
        Granted by Motor iNKS{entitlement.comp?.expires_at ? ` until ${new Date(entitlement.comp.expires_at).toLocaleDateString()}` : ''}.
      </div>
    );
  }
  return (
    <div className="text-[11px] text-white/50 flex flex-wrap items-center gap-2" data-testid="wrap-entitlement-guest">
      <Lock className="w-3 h-3" /> You're on Guest (price ranges). Pick a locked tier to upgrade or redeem a trial pass.
    </div>
  );
}
